import axios, { AxiosResponse } from 'axios';

import { getConfig } from '../config';
import { applyApplicationsMiddleware } from '../middleware/applications-middleware';
import { handleAuthError } from './handleAuthError';
import { handleFraudCompanyError } from './handleFraudCompany';
import { createAcceptLanguageHeader } from './helpers/createAcceptLanguageHeader';

export const isResponseSuccess = (response: AxiosResponse<unknown>) =>
  response.status >= 200 && response.status < 300;

export default function (token: string | null, baseURL?: string) {
  const config = getConfig();
  const headers = {
    'accept-language': createAcceptLanguageHeader(config.LANG),
  };
  const axiosConfig = {
    baseURL: baseURL || config.API_BASE,
    headers: {
      ...headers,
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  };

  const instance = axios.create(axiosConfig);

  // Axios doesn't wrap stuff as an Error object. In order for redux-actions
  // to recognize something as an error (and set action.error), the rejected
  // promise must have a payload which is an instance of Error.
  instance.interceptors.response.use(
    response => response,
    error => {
      handleFraudCompanyError(
        error.response?.status,
        error.response?.data?.error?.companyStatus
      );

      if (!(error instanceof Error)) {
        error = Object.assign(new Error('A HTTP request has failed'), error);
      }

      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    response => response,
    err => {
      const isLoggingOut = handleAuthError(
        err?.response?.status,
        err?.response?.data?.error
      );
      // If logout was triggered, return a never-resolving promise to suppress
      // error propagation. This prevents error UI flash before redirect completes.
      if (isLoggingOut) {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return new Promise(() => {});
      }
      return Promise.reject(err);
    }
  );

  applyApplicationsMiddleware(instance);

  return instance;
}
